// PayPayカードの利用明細CSVをZeniの取引インポート形式に変換する。
//
// PayPayカード会員メニューからダウンロードできる利用明細CSVは UTF-8(BOM付き) で、次の列を持つ:
//   利用日/キャンセル日, 利用店名・商品名, 利用者, 決済方法, 支払区分, 利用金額, 手数料,
//   支払総額, 当月支払金額, 翌月以降繰越金額, 調整額, 当月お支払日
// 楽天カードと同じく口座・カテゴリ情報が無いため、口座は呼び出し側で指定し、
// カテゴリは利用店名のキーワードから推定する（推定できなければ「その他(other)」）。

import {
  MERCHANT_CATEGORY,
  matchKeywordCategory,
  normalizeDate,
  parseAmount,
  resolveFallbackCategories,
  splitCsvLine,
  splitCsvLines,
  type CsvCategoryRef,
  type CsvImportRow,
  type CsvParseResult,
} from './csv-common'

export type PayPayCardCategoryRef = CsvCategoryRef
export type PayPayCardImportRow = CsvImportRow
export type PayPayCardParseResult = CsvParseResult

// 店名キーワード表より先に判定するPayPayカード固有の摘要。
// 「ＬＩＮＥＭＯ」は店名表に無く、先に並べないと後段の「ＬＩＮＥ」に食われるため順序が重要。
const PAYPAY_EXPENSE_OVERRIDE: ReadonlyArray<readonly [string, string]> = [
  // 通信（ソフトバンク系・PayPayカードの主要な引き落とし先）
  ['ＬＩＮＥＭＯ', 'telecom'], ['ワイモバイル', 'telecom'], ['Ｙ！ｍｏｂｉｌｅ', 'telecom'],
  ['オプテージ', 'telecom'], ['ｍｉｎｅｏ', 'telecom'], ['ｅｏ光', 'telecom'],
  // サブスク
  ['ＬＹＰプレミアム', 'entertainment'], ['ＬＩＮＥ', 'entertainment'],
  // PayPay残高チャージは口座間の資金移動だが、CSVからは移動先を特定できないため「その他」
  ['残高チャージ', 'other'], ['チャージ', 'other'],
]

interface ColumnMap {
  date: number
  desc: number
  payMethod: number
  payType: number
  gross: number
  fee: number
  total: number
}

// ヘッダー行から各列の位置を特定する。見つからない列は既定の並び順にフォールバックする。
function locateColumns(header: string[]): ColumnMap {
  const find = (keywords: string[], fallback: number) => {
    const idx = header.findIndex((h) => keywords.some((k) => h.includes(k)))
    return idx >= 0 ? idx : fallback
  }
  return {
    date: find(['利用日', '日付'], 0),
    desc: find(['店名', '商品名', '利用先'], 1),
    payMethod: find(['決済方法'], 3),
    payType: find(['支払区分'], 4),
    gross: find(['利用金額'], 5),
    fee: find(['手数料'], 6),
    total: find(['支払総額'], 7),
  }
}

// 決済方法やダウンロード日時が先頭に付くCSVもあり得るため、ヘッダー行を先頭数行から探す。
function findHeaderIndex(lines: string[]): number {
  const limit = Math.min(lines.length, 10)
  for (let i = 0; i < limit; i++) {
    const cells = splitCsvLine(lines[i])
    const hasDate = cells.some((c) => c.includes('利用日') || c.includes('日付'))
    const hasAmount = cells.some((c) => c.includes('利用金額') || c.includes('支払総額'))
    if (hasDate && hasAmount) return i
  }
  return 0
}

// 一括払いは既定なのでメモに残さない。分割・リボのみ支払区分を記録する。
const ONE_TIME_PAY_TYPES = new Set(['1回', '1回払い', '一括', '一括払い'])

// 支払区分と手数料をメモへ退避する。1回払いかつ手数料0円なら未設定。
function buildNote(payType: string, fee: number): string | undefined {
  const parts: string[] = []
  const normalized = payType.normalize('NFKC').trim()
  if (normalized !== '' && !ONE_TIME_PAY_TYPES.has(normalized)) parts.push(normalized)
  if (Number.isFinite(fee) && fee !== 0) parts.push(`手数料${fee}円`)
  return parts.length > 0 ? parts.join(' / ') : undefined
}

export function parsePayPayCardCsv(
  text: string,
  accountId: string,
  categories: ReadonlyArray<PayPayCardCategoryRef>,
): PayPayCardParseResult {
  const lines = splitCsvLines(text)
  if (lines.length === 0) return { rows: [], parsed: 0, ignored: 0 }

  const headerIndex = findHeaderIndex(lines)
  const col = locateColumns(splitCsvLine(lines[headerIndex]))

  const { expenseIds, fallbackExpense, fallbackIncome } = resolveFallbackCategories(categories)
  const expenseSet = new Set(expenseIds)

  const rows: PayPayCardImportRow[] = []
  let ignored = 0

  for (const line of lines.slice(headerIndex + 1)) {
    const cols = splitCsvLine(line)
    const date = normalizeDate(cols[col.date] ?? '')

    // 実際に請求される額は手数料込みの支払総額。欠けている場合のみ利用金額で代替する。
    const total = parseAmount(cols[col.total] ?? '')
    const gross = parseAmount(cols[col.gross] ?? '')
    const rawAmount = Number.isFinite(total) && total !== 0 ? total : gross
    if (!date || !Number.isFinite(rawAmount) || rawAmount === 0) {
      ignored++
      continue
    }

    // 決済方法（PayPayカード / PayPayクレジット等）が入る場合は摘要へ添える
    const merchant = (cols[col.desc] ?? '').trim()
    const description = merchant || (cols[col.payMethod] ?? '').trim() || 'PayPayカード利用'
    const isIncome = rawAmount < 0 // マイナス計上（キャンセル・返金）は収入扱い
    const amount = Math.abs(rawAmount)

    rows.push({
      date,
      type: isIncome ? 'income' : 'expense',
      category: isIncome
        ? fallbackIncome
        : matchKeywordCategory(description, PAYPAY_EXPENSE_OVERRIDE, expenseSet)
          ?? matchKeywordCategory(description, MERCHANT_CATEGORY, expenseSet)
          ?? fallbackExpense,
      description,
      amount,
      accountId,
      currency: 'JPY',
      note: buildNote(cols[col.payType] ?? '', parseAmount(cols[col.fee] ?? '')),
    })
  }

  return { rows, parsed: rows.length, ignored }
}
